// ===========================================
// HOOKS: Documentos (CORE)
// ===========================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { documentosService } from '../services/documentosService'
import { useNotificacionesModulo } from './useNotificacionesModulo'
import { PLANTILLAS_NOTIFICACION } from '../types/notificaciones'
import { useAuditor } from '@/hooks/useAuditor'
import { usePermissions } from '@/hooks/usePermissions'
import { assertPermiso } from '@/lib/permisos-guard'
import { rutaFichaCliente } from '@/lib/ruta-cliente'
import type {
  FiltrosDocumentos,
  MetadatosSubida,
  DocumentoUpdate,
  TipoEntidadDocumento,
  RolDocumento,
} from '../types/documentos'

// ===========================================
// Query Keys
// ===========================================

export const documentosKeys = {
  all: ['documentos'] as const,
  lists: () => [...documentosKeys.all, 'list'] as const,
  list: (filtros?: FiltrosDocumentos) => [...documentosKeys.lists(), filtros] as const,
  cliente: (clienteId: string) => [...documentosKeys.all, 'cliente', clienteId] as const,
  clienteCategoria: (clienteId: string, categoria?: string) =>
    [...documentosKeys.cliente(clienteId), categoria] as const,
  entidad: (tipo: TipoEntidadDocumento, id: string) =>
    [...documentosKeys.all, 'entidad', tipo, id] as const,
  details: () => [...documentosKeys.all, 'detail'] as const,
  detail: (id: string) => [...documentosKeys.details(), id] as const,
  vinculaciones: (documentoId: string) =>
    [...documentosKeys.all, 'vinculaciones', documentoId] as const,
  porVencer: (dias: number) => [...documentosKeys.all, 'por-vencer', dias] as const,
  busqueda: (query: string) => [...documentosKeys.all, 'busqueda', query] as const,
  estadisticas: (clienteId: string) => [...documentosKeys.all, 'estadisticas', clienteId] as const,
  estadisticasDetalladas: (clienteId: string) => [...documentosKeys.all, 'estadisticas-detalladas', clienteId] as const,
}

// ===========================================
// Hooks de Query
// ===========================================

/**
 * Hook para listar documentos con filtros opcionales
 */
export function useDocumentos(filtros?: FiltrosDocumentos) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: documentosKeys.list(filtros),
    queryFn: () => documentosService.listar(supabase!, filtros),
    enabled: !!supabase && !supabaseLoading,
  })
}

/**
 * Hook para listar documentos de un cliente específico
 */
export function useDocumentosCliente(clienteId: string, categoria?: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: documentosKeys.clienteCategoria(clienteId, categoria),
    queryFn: () => documentosService.listarPorCliente(supabase!, clienteId, categoria),
    enabled: !!supabase && !supabaseLoading && !!clienteId,
  })
}

/**
 * Hook para listar documentos vinculados a una entidad (polimórfico)
 */
export function useDocumentosEntidad(
  entidadTipo: TipoEntidadDocumento,
  entidadId: string,
  rol?: RolDocumento
) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: documentosKeys.entidad(entidadTipo, entidadId),
    queryFn: () => documentosService.obtenerPorEntidad(supabase!, entidadTipo, entidadId, rol),
    enabled: !!supabase && !supabaseLoading && !!entidadTipo && !!entidadId,
  })
}

/**
 * Hook para obtener un documento por ID
 */
export function useDocumento(id: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: documentosKeys.detail(id),
    queryFn: () => documentosService.obtenerPorId(supabase!, id),
    enabled: !!supabase && !supabaseLoading && !!id,
  })
}

/**
 * Hook para obtener vinculaciones de un documento
 */
export function useVinculacionesDocumento(documentoId: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: documentosKeys.vinculaciones(documentoId),
    queryFn: () => documentosService.obtenerVinculaciones(supabase!, documentoId),
    enabled: !!supabase && !supabaseLoading && !!documentoId,
  })
}

/**
 * Hook para obtener documentos próximos a vencer
 */
export function useDocumentosPorVencer(diasLimite: number = 30) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: documentosKeys.porVencer(diasLimite),
    queryFn: () => documentosService.obtenerPorVencer(supabase!, diasLimite),
    enabled: !!supabase && !supabaseLoading,
  })
}

/**
 * Hook para buscar documentos
 */
export function useBuscarDocumentos(query: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: documentosKeys.busqueda(query),
    queryFn: () => documentosService.buscar(supabase!, query),
    enabled: !!supabase && !supabaseLoading && query.length >= 2,
  })
}

/**
 * Hook para obtener estadísticas de documentos de un cliente
 */
export function useEstadisticasDocumentos(clienteId: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: documentosKeys.estadisticas(clienteId),
    queryFn: () => documentosService.obtenerEstadisticas(supabase!, clienteId),
    enabled: !!supabase && !supabaseLoading && !!clienteId,
  })
}

/**
 * Hook para obtener estadísticas detalladas por categoría (cantidad + tamaño)
 */
export function useEstadisticasDetalladas(clienteId: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: documentosKeys.estadisticasDetalladas(clienteId),
    queryFn: () => documentosService.obtenerEstadisticasDetalladas(supabase!, clienteId),
    enabled: !!supabase && !supabaseLoading && !!clienteId,
  })
}

// ===========================================
// Hooks de Mutation
// ===========================================

/**
 * Hook para subir un documento
 */
export function useSubirDocumento() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { subir } = useAuditor()
  const { puede } = usePermissions()
  const { organizacionId } = useOrganizacion()
  const { notificar, responsablesCliente, nombreCliente } = useNotificacionesModulo()

  return useMutation({
    mutationFn: ({
      file,
      clienteId,
      createdBy,
      metadatos,
    }: {
      file: File
      clienteId: string
      createdBy: string
      metadatos?: MetadatosSubida
    }) => {
      assertPermiso(puede, 'subirDocumento')
      return documentosService.subir(supabase!, file, clienteId, createdBy, metadatos, organizacionId)
    },
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries({ queryKey: documentosKeys.lists() })
      queryClient.invalidateQueries({ queryKey: documentosKeys.cliente(variables.clienteId) })
      queryClient.invalidateQueries({ queryKey: documentosKeys.estadisticas(variables.clienteId) })
      // Invalidar estadísticas detalladas para que CarpetasSidebar actualice los contadores
      queryClient.invalidateQueries({ queryKey: documentosKeys.estadisticasDetalladas(variables.clienteId) })
      if (variables.metadatos?.fecha_vencimiento) {
        // Matcher parcial por prefijo: invalida todas las queries porVencer(N)
        // sin importar el valor de `dias`. Equivalente a
        // [...documentosKeys.all, 'por-vencer'] pero más explícito.
        queryClient.invalidateQueries({
          queryKey: [...documentosKeys.all, 'por-vencer'],
        })
      }
      subir(data.id, {
        nombre: data.nombre,
        categoria: data.categoria,
        cliente_id: variables.clienteId,
        tamanio: data.archivo_size,
      })

      // Notif Fase 5: documento subido vinculado a cliente.
      // Reemplaza el bug pre-existente (notificarSistema al propio createdBy
      // que era auto-notificación). Ahora notifica a responsables del cliente,
      // excluyendo al actor. Dispara siempre (no solo cuando hay vencimiento).
      if (!supabase) return
      try {
        const clienteNombre = await nombreCliente(variables.clienteId)
        const actor = user?.name || user?.email || 'Alguien'
        const destinatarios = await responsablesCliente(variables.clienteId)
        if (destinatarios.length === 0) return
        const plantilla = PLANTILLAS_NOTIFICACION.documentoSubido(
          clienteNombre,
          data.nombre,
          data.categoria,
          actor,
        )
        await notificar({
          ...plantilla,
          destinatarios,
          enlace: rutaFichaCliente(variables.clienteId),
        })
      } catch (err) {
        console.error('[notificaciones] error notificando documento subido:', err)
      }
    },
  })
}

/**
 * Hook para actualizar metadatos de un documento
 */
export function useActualizarDocumento() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { editar } = useAuditor()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DocumentoUpdate }) => {
      assertPermiso(puede, 'editarDocumento')
      return documentosService.actualizar(supabase!, id, data)
    },
    onSuccess: (documento, variables) => {
      queryClient.invalidateQueries({ queryKey: documentosKeys.lists() })
      queryClient.invalidateQueries({ queryKey: documentosKeys.detail(documento.id) })
      if (documento.cliente_id) {
        queryClient.invalidateQueries({ queryKey: documentosKeys.cliente(documento.cliente_id) })
        // Invalidar estadísticas detalladas para actualizar contadores en CarpetasSidebar
        queryClient.invalidateQueries({ queryKey: documentosKeys.estadisticas(documento.cliente_id) })
        queryClient.invalidateQueries({ queryKey: documentosKeys.estadisticasDetalladas(documento.cliente_id) })
      }
      queryClient.invalidateQueries({ queryKey: ['documentos', 'por-vencer'] })
      editar('documento', variables.id, { cambios: variables.data })
    },
  })
}

/**
 * Hook para eliminar un documento
 */
export function useEliminarDocumento() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { eliminar } = useAuditor()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: async (id: string) => {
      assertPermiso(puede, 'eliminarDocumento')
      const documento = await documentosService.obtenerPorId(supabase!, id)
      await documentosService.eliminar(supabase!, id)
      return documento
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: documentosKeys.all })
      // Invalidar estadísticas específicas del cliente para actualizar contadores
      if (data.cliente_id) {
        queryClient.invalidateQueries({ queryKey: documentosKeys.estadisticas(data.cliente_id) })
        queryClient.invalidateQueries({ queryKey: documentosKeys.estadisticasDetalladas(data.cliente_id) })
      }
      eliminar('documento', data.id, {
        nombre: data.nombre,
        categoria: data.categoria,
      })
    },
  })
}

/**
 * Hook para vincular documento a una entidad
 */
export function useVincularDocumento() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { editar } = useAuditor()

  return useMutation({
    mutationFn: ({
      documentoId,
      entidadTipo,
      entidadId,
      workspace,
      rol,
    }: {
      documentoId: string
      entidadTipo: TipoEntidadDocumento
      entidadId: string
      workspace?: string
      rol?: RolDocumento
    }) => documentosService.vincular(supabase!, documentoId, entidadTipo, entidadId, workspace, rol),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: documentosKeys.entidad(variables.entidadTipo, variables.entidadId),
      })
      queryClient.invalidateQueries({
        queryKey: documentosKeys.vinculaciones(variables.documentoId),
      })
      editar('documento', variables.documentoId, {
        vinculacion: { tipo: variables.entidadTipo, id: variables.entidadId },
      })
    },
  })
}

/**
 * Hook para desvincular documento de una entidad
 */
export function useDesvincularDocumento() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { editar } = useAuditor()

  return useMutation({
    mutationFn: ({
      documentoId,
      entidadTipo,
      entidadId,
    }: {
      documentoId: string
      entidadTipo: TipoEntidadDocumento
      entidadId: string
    }) => documentosService.desvincular(supabase!, documentoId, entidadTipo, entidadId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: documentosKeys.entidad(variables.entidadTipo, variables.entidadId),
      })
      queryClient.invalidateQueries({
        queryKey: documentosKeys.vinculaciones(variables.documentoId),
      })
      editar('documento', variables.documentoId, {
        desvinculacion: { tipo: variables.entidadTipo, id: variables.entidadId },
      })
    },
  })
}

/**
 * Hook para obtener URL firmada de un documento
 * Inyecta el cliente Supabase autenticado (token fresco por request, ORR-138)
 */
export function useObtenerUrlDocumento() {
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: (path: string) => documentosService.obtenerUrl(supabase!, path),
  })
}

/**
 * Hook para descargar un documento
 * Inyecta el cliente Supabase autenticado (token fresco por request, ORR-138)
 */
export function useDescargarDocumento() {
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: async ({ path, nombreArchivo }: { path: string; nombreArchivo: string }) => {
      const blob = await documentosService.descargar(supabase!, path)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = nombreArchivo
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      return blob
    },
  })
}

// ===========================================
// Re-exports
// ===========================================

export type {
  FiltrosDocumentos,
  MetadatosSubida,
  DocumentoUpdate,
  TipoEntidadDocumento,
  RolDocumento,
  DocumentoVinculacion,
  CategoriaStats,
  EstadisticasDetalladas,
} from '../types/documentos'

export {
  CATEGORIAS_DOCUMENTO,
  ROL_DOCUMENTO_CONFIG,
  MIME_TYPES_PERMITIDOS,
  EXTENSIONES_PERMITIDAS,
  LIMITE_ARCHIVO_MB,
  LIMITE_ARCHIVO_BYTES,
  getIconoMimeType,
  formatFileSize,
  esArchivoPermitido,
  esTamanoPermitido,
} from '../types/documentos'
